import { useEffect, useRef, type CSSProperties } from "react";

/**
 * Переключатель подразделов shell: одинаковые кнопки с названием и необязательной
 * подсказкой для реквизитов документа и категорий файлов.
 *
 * Контракт управляемый: родитель передаёт упорядоченные пункты с уникальными id,
 * текущий value и onChange; сам хранит выбор и решает, какое содержимое показать.
 * Компонент не запрашивает данные и не меняет выбор при обновлении списка.
 *
 * Доступность: именованный nav объединяет обычные кнопки type="button", поэтому
 * Tab, Enter и пробел работают средствами браузера и не отправляют внешнюю форму.
 * aria-current="page" отмечает выбранный подраздел; название и подсказка остаются
 * в кнопке, а существующий класс сохраняет оформление и адаптивную сетку.
 * Это навигация по подразделам, а не tablist с отдельным клавиатурным контрактом.
 * После выбора центрируется только полоса кнопок: страница и карточка не скроллятся.
 */
export function SectionSwitcher<Value extends string>({
  items,
  value,
  onChange,
  ariaLabel,
}: {
  items: readonly { id: Value; label: string; hint?: string }[];
  value: Value;
  onChange: (value: Value) => void;
  ariaLabel: string;
}) {
  const nav = useRef<HTMLElement>(null);

  useEffect(() => {
    const active = nav.current?.querySelector<HTMLElement>(
      '[aria-current="page"]',
    );
    if (!nav.current || !active) return;
    nav.current.scrollTo?.({
      left:
        active.offsetLeft - (nav.current.clientWidth - active.offsetWidth) / 2,
    });
  }, [value]);

  return (
    <nav
      ref={nav}
      className="dw-card__subnav"
      style={{ "--section-count": items.length } as CSSProperties}
      aria-label={ariaLabel}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-current={value === item.id ? "page" : undefined}
          onClick={() => onChange(item.id)}
        >
          <span>{item.label}</span>
          {item.hint ? <small>{item.hint}</small> : null}
        </button>
      ))}
    </nav>
  );
}
